import React, { useState, useEffect } from "react";
import { FileText, Users, Check } from "lucide-react";
import ContractModal from "./ContractModal";
import NewClientModal from "./NewClientModal";
import ResourceForm from "./ResourceModal";
import "./ResourcifiCard.css";

const ResourcifiCard = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState(null);

  // Get API URL with fallback
  const getApiUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    console.log("🔧 DEBUG: REACT_APP_API_URL from env:", apiUrl);
    console.log("🔧 DEBUG: All env vars:", process.env);

    if (!apiUrl || apiUrl === "undefined") {
      console.error("REACT_APP_API_URL is not defined in .env file");
      return "http://localhost:8000"; // fallback
    }

    console.log("🔧 DEBUG: Using API URL:", apiUrl);
    return apiUrl;
  };

  // Function to extract contact ID from HubSpot URL
  const extractContactIdFromUrl = (url) => {
    try {
      const hubspotUrlPattern = /\/contacts\/\d+\/record\/0-1\/(\d+)/;
      const match = url.match(hubspotUrlPattern);
      if (match) {
        return match[1]; // Return the contact ID (751 in the example)
      }

      // Handle direct contact ID in URL parameters
      const urlObj = new URL(url);
      const contactIdFromParams = urlObj.searchParams.get("contactId");
      if (contactIdFromParams) {
        return contactIdFromParams;
      }

      return null;
    } catch (error) {
      console.error("Error extracting contact ID from URL:", error);
      return null;
    }
  };

  // Get contact ID from URL params or HubSpot URL
  const getContactId = () => {
    const params = new URLSearchParams(window.location.search);

    // Try to get contact ID from URL parameters first
    let contactId = params.get("contactId");

    // Try to extract from HubSpot URL parameter
    if (!contactId) {
      const hubspotUrl = params.get("hubspotUrl");
      if (hubspotUrl) {
        contactId = extractContactIdFromUrl(hubspotUrl);
      }
    }

    // Try to extract from current page URL if it's a HubSpot URL
    if (!contactId) {
      const currentUrl = window.location.href;
      contactId = extractContactIdFromUrl(currentUrl);
    }

    // Try portal ID as fallback
    if (!contactId) {
      contactId = params.get("portalId");
    }

    return contactId || "9960901"; // fallback for testing
  };

  // Get component name from URL params to determine which modal to show
  const getComponentName = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("componentName");
  };

  // Fetch contact details from HubSpot API
  const fetchContactDetails = async () => {
    const contactId = getContactId();
    const apiUrl = getApiUrl();

    setContactLoading(true);
    setContactError(null);

    try {
      console.log("Fetching contact details for ID:", contactId);
      const response = await fetch(
        `${apiUrl}/api/hubspot/contact/${contactId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setContactDetails(data?.data);
        console.log("Contact details fetched successfully:", data.contact);
      } else {
        setContactError(data.message || "Failed to fetch contact details");
        console.error("Error fetching contact details:", data.message);
      }
    } catch (error) {
      console.error("Error fetching contact details:", error);
      setContactError(error.message || "Failed to fetch contact details");
    } finally {
      setContactLoading(false);
    }
  };

  // Auto-open modal based on componentName parameter and fetch contact details on page load
  useEffect(() => {
    // Log URL information for debugging
    console.log("Current URL:", window.location.href);
    console.log("URL Search params:", window.location.search);

    const contactId = getContactId();
    console.log("Extracted contact ID:", contactId);

    // Fetch contact details as soon as the page loads
    if (contactId && contactId !== "9960901") {
      fetchContactDetails();
    }

    // Handle modal opening based on componentName parameter
    const componentName = getComponentName();
    console.log("Component name from URL:", componentName);

    if (componentName) {
      if (
        componentName === "contractInvoive" ||
        componentName === "contractInvoice"
      ) {
        setActiveModal("contract");
      } else if (componentName === "requestResource") {
        setActiveModal("resources");
      } else if (componentName === "clientCard") {
        setActiveModal("clientCard");
      }
    }
  }, []);

  const handleContractSubmit = async (formData) => {
    setLoading(true);
    try {
      const apiUrl = getApiUrl();

      // Check if we're updating an existing deal or creating a new one
      const isUpdating = formData.updateExistingDeal && formData.selectedDealId;

      if (isUpdating) {
        console.log("Updating existing deal:", formData.selectedDealId);
        console.log("Update data being sent:", formData);

        // Call the update deal API endpoint
        const response = await fetch(
          `${apiUrl}/api/hubspot/deals/${formData.selectedDealId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          }
        );

        const data = await response.json();
        console.log("Update deal response:", data);

        if (data.success) {
          setSuccessMessage(
            `Deal "${data.deal?.name || "Deal"}" updated successfully!`
          );
          setShowSuccess(true);
        } else {
          console.error("Deal update error:", data);
          alert(`Error updating deal: ${data.message || "Update failed"}`);
        }
      } else {
        console.log("Creating new contract deal");
        console.log("Form data being sent:", formData);

        // Transform form data to match backend expectations for new deals
        const transformedData = {
          ...formData,
          // Map new field names to old field names that backend might expect
          projectDescription: formData.projectTitle, // backend might expect projectDescription
          pricing: formData.totalPrice, // backend might expect pricing instead of totalPrice
        };

        console.log("Transformed data:", transformedData);

        // Call the create deal API endpoint
        const response = await fetch(`${apiUrl}/api/contract/submit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contactId: getContactId(),
            formData: transformedData,
          }),
        });

        const data = await response.json();
        console.log("Create deal response:", data);

        if (data.success) {
          setSuccessMessage("Contract deal created successfully!");
          setShowSuccess(true);
        } else {
          console.error("Backend validation error:", data);
          alert(`Error: ${data.message || "Validation failed"}`);

          // If there are specific field errors, log them
          if (data.errors) {
            console.error("Field errors:", data.errors);
          }
        }
      }
    } catch (error) {
      console.error("Error submitting contract:", error);
      alert("Failed to submit contract request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResourceSubmit = async (formData) => {
    setLoading(true);
    try {
      const apiUrl = getApiUrl();

      console.log("🔍 ResourcifiCard: Received formData:", formData);
      console.log("🔍 ResourcifiCard: formData type:", typeof formData);
      console.log("🔍 ResourcifiCard: Is FormData?", formData instanceof FormData);

      // Check if formData is FormData (file upload) or regular object
      const isFormDataWithFile = formData instanceof FormData;

      if (isFormDataWithFile) {
        // File upload case - send FormData directly
        console.log("📎 ResourcifiCard: Sending FormData with file");
        
        const response = await fetch(`${apiUrl}/api/resource/submit`, {
          method: "POST",
          // Don't set Content-Type header for FormData - browser will set it with boundary
          body: formData,
        });

        const data = await response.json();
        console.log("Create resource deal response:", data);

        if (data.success) {
          setSuccessMessage("Resource deal created successfully!");
          setShowSuccess(true);
        } else {
          console.error("Backend validation error:", data);
          alert(`Error: ${data.message || "Validation failed"}`);
        }
      } else {
        // Regular JSON case
        // Check if formData already has the structure we need (from ResourceModal)
        const isAlreadyStructured = formData.contactId && formData.formData;

        if (isAlreadyStructured) {
          // ResourceModal already structured it correctly - send as-is
          console.log("📝 ResourcifiCard: Sending pre-structured data");
          console.log("📝 Data structure:", JSON.stringify(formData, null, 2));

          const response = await fetch(`${apiUrl}/api/resource/submit`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          });

          const data = await response.json();
          console.log("Create resource deal response:", data);

          if (data.success) {
            setSuccessMessage("Resource deal created successfully!");
            setShowSuccess(true);
          } else {
            console.error("Backend validation error:", data);
            alert(`Error: ${data.message || "Validation failed"}`);
          }
        } else {
          // Old format - wrap it for backward compatibility
          console.log("📝 ResourcifiCard: Wrapping old format data");

          const response = await fetch(`${apiUrl}/api/resource/submit`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              contactId: getContactId(),
              formData: formData,
            }),
          });

          const data = await response.json();
          console.log("Create resource deal response:", data);

          if (data.success) {
            setSuccessMessage("Resource deal created successfully!");
            setShowSuccess(true);
          } else {
            console.error("Backend validation error:", data);
            alert(`Error: ${data.message || "Validation failed"}`);
          }
        }
      }
    } catch (error) {
      console.error("Error submitting resource request:", error);
      alert("Failed to submit resource request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewClientSubmit = async (formData) => {
    setLoading(true);
    try {
      const apiUrl = getApiUrl();
      console.log("Submitting new client request");
      console.log("New client form data being sent:", formData);

      // Sanitize formData to remove nested objects and complex data
      const sanitizeFormData = (data) => {
        const sanitized = {};
        Object.entries(data).forEach(([key, value]) => {
          // Skip nested objects, arrays, and functions
          if (value !== null && value !== undefined && 
              typeof value !== 'object' && 
              typeof value !== 'function' && 
              !Array.isArray(value)) {
            sanitized[key] = String(value).trim();
          } else if (key === 'modalType' || key === 'dealStage' || key === 'selectedDealId') {
            // Keep essential fields even if they're strings
            sanitized[key] = value;
          }
        });
        return sanitized;
      };

      const cleanFormData = sanitizeFormData(formData);
      console.log("Sanitized form data:", cleanFormData);

      // Transform form data to match backend expectations
      const transformedData = {
        contactId: getContactId(),
        formData: cleanFormData,
        dealStage: formData.dealStage,
        selectedDealId: formData.selectedDealId,
        updateExistingDeal: true, // New Client modal always updates existing deals
      };

      console.log("Transformed new client data:", transformedData);

      // Call the new client API endpoint
      const response = await fetch(`${apiUrl}/api/new-client/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(transformedData),
      });

      const data = await response.json();
      console.log("New client response:", data);

      if (data.success) {
        setSuccessMessage(
          data.message || "New client request submitted successfully!"
        );
        setShowSuccess(true);
      } else {
        console.error("Backend validation error:", data);
        alert(`Error: ${data.message || "Validation failed"}`);

        // If there are specific field errors, log them
        if (data.errors) {
          console.error("Field errors:", data.errors);
        }
      }
    } catch (error) {
      console.error("Error submitting new client request:", error);
      alert("Failed to submit new client request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="resourcifi-card">
      {/* Contact Information Section */}
      {contactLoading && (
        <div className="contact-loading">
          <p>Loading contact details...</p>
        </div>
      )}

      {contactError && (
        <div className="contact-error">
          <p>Error loading contact: {contactError}</p>
        </div>
      )}

      {/* Contract Modal */}
      <ContractModal
        isOpen={activeModal === "contract"}
        onClose={() => setActiveModal(null)}
        onSubmit={handleContractSubmit}
        loading={loading}
        showSuccess={showSuccess}
        successMessage={successMessage}
        contactDetails={contactDetails}
        contactId={getContactId()}
        portalId={new URLSearchParams(window.location.search).get("portalId")}
      />

      {/* Resource Form */}
      {activeModal === "resources" && (
        <ResourceForm
          onSubmit={handleResourceSubmit}
          loading={loading}
          showSuccess={showSuccess}
          successMessage={successMessage}
          contactDetails={contactDetails}
        />
      )}

      <NewClientModal
        isOpen={activeModal === "clientCard"}
        onClose={() => setActiveModal(null)}
        onSubmit={handleNewClientSubmit}
        loading={loading}
        showSuccess={showSuccess}
        successMessage={successMessage}
        contactDetails={contactDetails}
        contactId={getContactId()}
        portalId={new URLSearchParams(window.location.search).get("portalId")}
      />
    </div>
  );
};

export default ResourcifiCard;
